import { registerIntegration } from "@p0security/shared/integrations/registry";
import {
  type AnyFilterSchema,
  Registration,
} from "@p0security/shared/integrations/types";

import { JiraAiAccesses } from "./accesses.ts";
import { JiraAiComponents } from "./components.ts";
import { JiraAiIconUrl } from "./icon.ts";

// Rule filter values compile to regular expressions, so a rule can say, e.g.,
// that write tools on `PROD.*` need approval. `site` matches the site's item
// id. `role` matches the name of the role the grant gives: P0's own role in
// a company-managed project, or the requested role in a team-managed one.
const JiraAiFilters = {
  site: { keys: ["name"] },
  project: { keys: ["name"] },
  tool: { keys: ["name"] },
  role: { keys: ["name"] },
} as const satisfies AnyFilterSchema;
export type JiraAiFilters = typeof JiraAiFilters;

// Keyed by component, so a new component doesn't compile until it has one.
const ITEM_KINDS: Record<keyof typeof JiraAiComponents, string> = {
  connector: "Connector",
  "pool-account": "Agent account",
  site: "Jira site",
};
// `Registration` hands `itemKind` any string, not just a component key.
const itemKinds: Partial<Record<string, string>> = ITEM_KINDS;

export const JiraAiRegistration = Registration({
  accesses: JiraAiAccesses,
  category: "resource",
  components: JiraAiComponents,
  // A path here becomes a link in the product, so there's none until the
  // docs page exists (INT-281).
  docs: {},
  filters: JiraAiFilters,
  identifier: "ID",
  itemKind: (component) => itemKinds[component] ?? component,
  label: "Jira agent access",
  logoUrl: JiraAiIconUrl,
  // No feature flag: new integrations ship as beta from their first PR.
  maturity: "beta",
  requestType: "requestable",
  // A connector alone grants nothing, so jira-ai counts as installed once a
  // site is.
  useMapping: [{ component: "site", use: "iam_write" }],
});

registerIntegration("jira-ai", JiraAiRegistration);
